import * as Sentry from '@sentry/react';
import { Button, Result } from 'antd';
import { logger } from '@ui/utils/logger';
import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onReset?: () => void;
}

interface State {
    hasError: boolean;
    error?: Error;
    errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log error
        logger.error('Uncaught error', { error, errorInfo });

        // Store error info in state for debugging
        this.setState({
            error,
            errorInfo,
        });

        // Log to Sentry
        Sentry.captureException(error, {
            contexts: {
                react: {
                    componentStack: errorInfo.componentStack,
                },
            },
        });
    }

    private handleReset = () => {
        this.setState({ hasError: false, error: undefined, errorInfo: undefined });
        this.props.onReset?.();
    };

    public render() {
        if (this.state.hasError) {
            // Use custom fallback if provided
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default error UI
            return (
                <div
                    style={{
                        padding: '40px',
                        minHeight: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Result
                        status="error"
                        title="Something went wrong"
                        subTitle="We're sorry for the inconvenience. Please try refreshing the page or contact support if the problem persists."
                        extra={[
                            <Button
                                type="primary"
                                onClick={this.handleReset}
                                key="reset"
                            >
                                Try Again
                            </Button>,
                            <Button
                                onClick={() => (window.location.href = '/')}
                                key="home"
                            >
                                Go Home
                            </Button>,
                        ]}
                    >
                        {import.meta.env.DEV && this.state.error && (
                            <div style={{ textAlign: 'left', marginTop: '24px' }}>
                                <details style={{ whiteSpace: 'pre-wrap', fontSize: '12px', color: '#666' }}>
                                    <summary style={{ cursor: 'pointer', marginBottom: '8px', fontWeight: 600 }}>
                                        Error Details (Development Only)
                                    </summary>
                                    <div
                                        style={{
                                            padding: '12px',
                                            background: '#f5f5f5',
                                            borderRadius: '4px',
                                            overflow: 'auto',
                                        }}
                                    >
                                        <strong>Error:</strong> {this.state.error.message}
                                        <br />
                                        <br />
                                        <strong>Stack Trace:</strong>
                                        <pre>{this.state.error.stack}</pre>
                                        {this.state.errorInfo && (
                                            <>
                                                <strong>Component Stack:</strong>
                                                <pre>{this.state.errorInfo.componentStack}</pre>
                                            </>
                                        )}
                                    </div>
                                </details>
                            </div>
                        )}
                    </Result>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
